import { dehydrate } from "@tanstack/react-query";
import { GetServerSidePropsContext } from "next";
import { NextSeo } from "next-seo";
import AuthorContainer from "@/components/container/Author";
import { ContentAuthor } from "@/graphql/gql/graphql";
import { getSingleAuthor, getAuthors } from "@/utils/api";
import { loadHeader, loadRecentArticles } from "@/utils/ssr";

interface AuthorProps {
  author: ContentAuthor;
  authors: ContentAuthor[];
}

const AuthorPage = ({ author, authors }: AuthorProps) => (
  <>
    <NextSeo title={author.name} />
    <AuthorContainer author={author} authors={authors} />
  </>
);

export async function getServerSideProps({
  params,
}: GetServerSidePropsContext) {
  if (!params?.author) {
    return {
      notFound: true,
    };
  }

  try {
    const [menuClient, authorDetails, authorsList] = await Promise.all([
      loadHeader(),
      getSingleAuthor({ slug: params.author.toString() }),
      getAuthors({ limit: 12 }),
    ]);

    const author = authorDetails?.getSingleAuthor;

    if (!author) {
      return {
        notFound: true,
      };
    }

    await loadRecentArticles(menuClient, {
      limit: 16,
      author: {
        id: author.id,
      },
    });

    const authors =
      authorsList?.getAuthors?.authors
        ?.map((auth) => {
          if (auth?.entity) {
            return {
              id: auth.entity.id,
              name: auth.entity.name,
              slug: auth.entity.slug || "",
              profileImageUrl: auth.entity.profileImageUrl,
            };
          }
          return null;
        })
        .filter(Boolean) ?? [];

    return {
      props: {
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(menuClient))),
        author,
        authors,
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
}

export default AuthorPage;
